import { Component, forwardRef, Input, OnInit, DoCheck } from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
  Validator,
  ValidationErrors,
  NG_VALIDATORS,
  UntypedFormGroup,
  Validators,
  AbstractControl
} from '@angular/forms';
import { ICurrency } from '../../interfaces/currency.interface';
import { FormInputField } from '../../services/form-fields-generator.service';
import { FormAllFieldsValidationService } from '../../services/form-all-fields-validation.service';
import { RangeValidator } from '../../../validators/functions/range-validation';

@Component({
  selector: 'ab-currency-input',
  templateUrl: './currency-input.component.html',
  styleUrls: ['./currency-input.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CurrencyInputComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CurrencyInputComponent),
      multi: true
    }
  ]
})
export class CurrencyInputComponent
  implements ControlValueAccessor, Validator, OnInit, DoCheck {
  @Input() maxlength: number;
  @Input() placeholder: string;
  @Input() field: FormInputField;
  @Input() formControl: UntypedFormControl;
  @Input() amountRequired: boolean;

  @Input() set rangeMin(value) {
    if (!this._rangeMin) {
      this._rangeMin = value;
      return;
    }

    this._rangeMin = value;

    this.currencyForm.get('amount').clearValidators();

    const validatiors = [];

    if (this.amountRequired) {
      validatiors.push(Validators.required);
    }

    if (this.rangeMax && this.rangeMin) {
      validatiors.push(RangeValidator(this._rangeMin, this.rangeMax));
    }

    this.currencyForm.get('amount').setValidators(validatiors);
    this.currencyForm.get('amount').updateValueAndValidity();
  }

  @Input() rangeMax: number;
  @Input() amountExceedsLimit: boolean;
  private _notAllowedCurrency: boolean;
  private _rangeMin: number;

  get rangeMin() {
    return this._rangeMin;
  }

  @Input('notAllowedCurrency') set allowedCurrency(value: boolean) {
    if (this._notAllowedCurrency && this._notAllowedCurrency !== value) {
      this._notAllowedCurrency = value;
      this.currencyCode.setErrors(null);

      this.currencyForm.updateValueAndValidity();
      return;
    }

    this._notAllowedCurrency = value;
  }

  get notAllowedCurrency(): boolean {
    return this._notAllowedCurrency;
  }

  @Input() currencyInput: string;

  public currencyForm = new UntypedFormGroup({
    amount: new UntypedFormControl(null),
    currencyCode: new UntypedFormControl(null)
  });

  private onChange;
  private onTouched;
  private initValueDone = false;
  get currencyCode() {
    return this.currencyForm.get('currencyCode');
  }
  constructor(
    private formAllFieldsValidationService: FormAllFieldsValidationService
  ) {}

  ngOnInit() {
    this.formAllFieldsValidationService.addSubForms(this.currencyForm);

    const validatiors = [];

    if (this.amountRequired) {
      validatiors.push(Validators.required);
    }

    if (this.rangeMax && this.rangeMin) {
      validatiors.push(RangeValidator(this.rangeMin, this.rangeMax));
    }

    this.currencyForm.get('amount').setValidators(validatiors);
  }

  ngDoCheck() {
    if (this.amountExceedsLimit || this.notAllowedCurrency) {
      this.currencyForm.updateValueAndValidity();
    }

    if (this.formControl.hasError('lessThan')) {
      this.currencyForm.get('amount').setErrors({
        lessThan: true
      });
    } else if (this.currencyForm.get('amount').hasError('lessThan')) {
      this.currencyForm.get('amount').setErrors(null);
      this.currencyForm.updateValueAndValidity();
    }
  }

  writeValue(value: ICurrency) {
    if (value) {
      if (
        this.currencyForm.get('amount').value !== null &&
        value.amount === null
      ) {
        this.currencyForm.reset(value, { emitEvent: false });
        return;
      }

      if (this.currencyForm.get('amount').value === value.amount) {
        return this.currencyForm.patchValue({
          currencyCode: value.currencyCode
        });
      }

      if (!value.amount || !value.currencyCode) {
        this.currencyForm.patchValue(value);
      } else {
        this.currencyForm.setValue(value);
      }
    }
  }

  registerOnChange(fn: any) {
    this.onChange = fn;
    this.currencyForm.valueChanges.subscribe(value => this.change(value));
  }

  change(value) {
    if (this.initValueDone && this.currencyForm.get('amount').dirty) {
      this.onChange(value);
    } else {
      this.initValueDone = true;
    }
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  touch() {
    this.onTouched();
  }

  validate(): ValidationErrors | null {
    if (
      this.rangeMin &&
      this.rangeMax &&
      this.currencyForm.value.amount !== null
    ) {
      const validation = RangeValidator(
        this.rangeMin,
        this.rangeMax
      )({
        value: this.currencyForm.value.amount
      } as AbstractControl);

      if (validation) {
        return validation;
      }
    }

    if (this.amountRequired && !this.currencyForm.value.amount) {
      return { required: true };
    }

    if (this.amountExceedsLimit) {
      this.currencyForm.get('amount').setErrors({
        amountExceedsLimit: true
      });
      return { amountExceedsLimit: true };
    }

    if (this.notAllowedCurrency) {
      this.currencyForm.get('currencyCode').setErrors({
        notAllowedCurrency: true
      });
      return { notAllowedCurrency: true };
    }

    return null;
  }
}
